// 카리아카 App.tsx 포크. 화면들이 useContext(Page_settings) 로 쓰는 계약
// (get/post/put/patch/delete/download/showAlert/showConfirm/showModal/toggleLoadingBar/loadImage)을 그대로 제공한다.
// 교육 전용 기능(파이어베이스 알림·중복 학생 모달·갤러리·알림 토스트·점검 모드)은 뺐다.
import { AxiosInstance } from "axios";
import { inject, observer } from "mobx-react";
import React from "react";
import { useNavigate } from "react-router-dom";

import Content from "./components/content/content";
import Layout from "./components/content/layout";
import Loading_bar from "./components/control/loading_bar";
import CommonModalJs from "./components/control/modal";
import { HEADER, RESULT_CODE } from "./config/const";
import api_interceptor from "./config/api_interceptor";
import { Page_settings } from "./config/page_settings.js";
import strings from "./lang/strings";
import { rootStore } from "./mobx/store";
import { formatApiError, resolveApiError } from "./utils/errorMessage";
import BusinessRegistrationRetryModal from "./pages/corp/businessRegistration/modal/BusinessRegistration_retry_modal";
import "./assets/fonts/fonts.css";

const BUCKET_URL = process.env.REACT_APP_BUCKET_URL;
// modal.js 는 JS 라 props 타입이 없다.
const CommonModal = CommonModalJs as React.ComponentType<any>;

type Callback = (data: any) => void;
type ErrorCallback = (error: string) => void;
type Request = (
  uri: string,
  params: any,
  cb?: Callback,
  errCb?: ErrorCallback,
  showProgress?: boolean
) => Promise<void>;

interface AppProps {
  rootStore?: any;
  navigate?: (path: string) => void;
}

interface ModalState {
  show: boolean;
  title: string;
  text: string;
  subText: string;
  okBtn: string;
  cancelBtn: string;
  cb: ((res: any) => void) | null;
}

interface AppState {
  pageLoading: boolean;
  modal: ModalState;
  toggleLoadingBar: (isLoading: boolean) => void;
  showModal: (title: string, text: string, okBtn: string, cancelBtn: string, cb?: (res: any) => void) => void;
  showAlert: (text: string, cb?: (res: any) => void, okBtn?: string) => void;
  showConfirm: (text: string, cb?: (res: any) => void, okBtn?: string, cancelBtn?: string) => void;
  get: Request;
  post: Request;
  put: Request;
  patch: Request;
  delete: Request;
  download: (method: string, uri: string, params: any, filename?: string, showProgress?: boolean) => Promise<void>;
  loadImage: (...url: string[]) => string;
}

const withNavigation = (Component: React.ComponentType<any>) => {
  return (props: any) => {
    const navigate = useNavigate();
    return <Component {...props} navigate={navigate} />;
  };
};

class App extends React.Component<AppProps, AppState> {
  private api: AxiosInstance = api_interceptor;

  constructor(props: AppProps) {
    super(props);

    const toggleLoadingBar = (isLoading: boolean) => this.setState({ pageLoading: isLoading });

    const showModal: AppState["showModal"] = (title, text, okBtn, cancelBtn, cb) => {
      this.setState({
        modal: { show: true, title, text, subText: "", okBtn, cancelBtn, cb: cb ?? null },
      });
    };
    const showAlert: AppState["showAlert"] = (text, cb, okBtn = strings.modal.confirm) =>
      showModal(strings.modal.alert, text, okBtn, "", cb);
    const showConfirm: AppState["showConfirm"] = (
      text,
      cb,
      okBtn = strings.modal.ok,
      cancelBtn = strings.modal.no
    ) => showModal(strings.modal.confirm, text, okBtn, cancelBtn, cb);

    const handleApiError = (e: any, uri: string) => {
      if (Number(e.result_code) === RESULT_CODE.EMPTY_TOKEN) {
        if (!window.location.pathname.includes("/login")) {
          this.props.navigate?.("/login");
        }
        rootStore.signOut();
        return;
      }
      // 토큰 만료는 interceptor 가 갱신 후 재시도한다.
      if (Number(e.result_code) === RESULT_CODE.TOKEN_EXPIRED) return;
      showAlert(formatApiError(e, uri));
    };

    const request = async (
      method: string,
      uri: string,
      params: any,
      cb?: Callback,
      errCb?: ErrorCallback,
      showProgress = true
    ) => {
      if (showProgress) toggleLoadingBar(true);
      this.api.defaults.responseType = "json";

      try {
        const { data } =
          method !== "GET"
            ? await (this.api as any)[method.toLowerCase()](uri, params)
            : await this.api.get(uri, { params });

        if (showProgress) toggleLoadingBar(false);

        if (Number(data.result_code) === RESULT_CODE.SUCCESS) {
          cb?.(data.result_data);
        } else if (Number(data.result_code) === RESULT_CODE.CUSTOM_ERROR) {
          errCb?.(data.result_msg);
        } else {
          errCb ? errCb(data.result_msg) : handleApiError(data, uri);
        }
      } catch (err) {
        if (showProgress) toggleLoadingBar(false);
        // HTTP 4xx/5xx 여도 사유는 본문(result_msg)에 있다. 사유가 없으면 상태코드를 한글로 바꿔 보여준다.
        const resolved = resolveApiError(err, uri);
        console.error(`[API ERROR] ${uri} →`, resolved.message, resolved.detail ?? "", err);
        errCb ? errCb(resolved.message) : showAlert(formatApiError(err, uri));
      }
    };

    const download: AppState["download"] = async (method, uri, params, filename, showProgress = true) => {
      if (showProgress) toggleLoadingBar(true);
      this.api.defaults.headers.common[HEADER.AUTH_TOKEN] = rootStore.token;
      this.api.defaults.responseType = "blob";

      try {
        const response: any =
          method !== "GET"
            ? await (this.api as any)[method.toLowerCase()](uri, params)
            : await this.api.get(uri, { params });

        if (showProgress) toggleLoadingBar(false);
        const disposition = response.headers.get("content-disposition");
        const raw = disposition?.split("filename=")[1]?.replace(/"/g, "");
        let name = filename || raw || "downloaded_file";
        try {
          name = decodeURIComponent(name);
        } catch {
          // 인코딩되지 않은 파일명은 그대로 쓴다.
        }
        const url = window.URL.createObjectURL(new Blob([response.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", name);
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);
      } catch (err) {
        console.error(`[API DOWNLOAD ERROR] ${uri} →`, err);
        if (showProgress) toggleLoadingBar(false);
        showAlert(formatApiError(err, uri));
      } finally {
        this.api.defaults.responseType = "json";
      }
    };

    this.state = {
      pageLoading: false,
      modal: { show: false, title: "", text: "", subText: "", okBtn: "", cancelBtn: "", cb: null },
      toggleLoadingBar,
      showModal,
      showAlert,
      showConfirm,
      get: (uri, params, cb, errCb, showProgress = true) => request("GET", uri, params, cb, errCb, showProgress),
      post: (uri, params, cb, errCb, showProgress = true) => request("POST", uri, params, cb, errCb, showProgress),
      put: (uri, params, cb, errCb, showProgress = true) => request("PUT", uri, params, cb, errCb, showProgress),
      patch: (uri, params, cb, errCb, showProgress = true) => request("PATCH", uri, params, cb, errCb, showProgress),
      delete: (uri, params, cb, errCb, showProgress = true) =>
        request("DELETE", uri, { data: params }, cb, errCb, showProgress),
      download,
      loadImage: (...url) => {
        const path = String(url);
        if (path.startsWith("http://") || path.startsWith("https://")) return path;
        return BUCKET_URL + "/" + path;
      },
    };
  }

  componentDidMount() {
    rootStore.initializeToken();
  }

  onModalClose = (res: any) => {
    this.setState(
      (state) => ({ modal: { ...state.modal, show: false } }),
      () => this.state.modal.cb?.(res)
    );
  };

  render() {
    return (
      <Page_settings.Provider value={this.state}>
        <Layout>
          <Content rootStore={rootStore} />
          <CommonModal {...this.state.modal} onModalClose={this.onModalClose} />
          {this.state.pageLoading && <Loading_bar />}
          {rootStore.businessRegistrationRetryModal && (
            <BusinessRegistrationRetryModal
              onClose={() => rootStore.setBusinessRegistrationRetryModal(false)}
            />
          )}
        </Layout>
      </Page_settings.Provider>
    );
  }
}

export default withNavigation(inject("rootStore")(observer(App)));
